import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Cropper from 'react-easy-crop'
import { Upload, RotateCw, Trash2 } from 'lucide-react'
import { apiError } from '../lib/api'

// Campo de upload de imagem com recorte no front (react-easy-crop): o usuário
// escolhe o arquivo, ajusta zoom/posição/rotação dentro da moldura e o componente
// exporta um Blob JPEG (canvas). O upload em si acontece no fim do wizard, via
// uploadImage() abaixo.
//
// value: { blob, previewUrl } | null   onChange(value)
export default function ImageCropField({ value, onChange, aspect = 4 / 3, hint }) {
  const { t } = useTranslation()
  const [src, setSrc] = useState(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [area, setArea] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => () => { if (src) URL.revokeObjectURL(src) }, [src])

  // O accept do <input> não vale para arrastar-e-soltar: um PDF ou um GeoTIFF caía aqui
  // e só falhava depois, no canvas, sem mensagem.
  const pick = (e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0] || e.target.files?.[0]
    if (!f) return
    setError(null)
    if (!String(f.type || '').startsWith('image/')) {
      setError(t('admin:wizard.image_invalid'))
      return
    }
    if (src) URL.revokeObjectURL(src)
    setSrc(URL.createObjectURL(f))
    setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0)
    onChange?.(null)
  }

  const onCropComplete = useCallback((_, px) => setArea(px), [])

  const confirm = async () => {
    if (!src || !area) return
    setBusy(true)
    setError(null)
    try {
      const blob = await cropToJpeg(src, area, rotation)
      const previewUrl = URL.createObjectURL(blob)
      onChange?.({ blob, previewUrl })
      URL.revokeObjectURL(src)
      setSrc(null)
    } catch (err) {
      setError(err.message || t('admin:wizard.image_failed'))
    } finally { setBusy(false) }
  }

  const clear = () => {
    if (value?.previewUrl) URL.revokeObjectURL(value.previewUrl)
    if (src) URL.revokeObjectURL(src)
    setSrc(null)
    onChange?.(null)
  }

  if (value?.previewUrl) {
    return (
      <div className="space-y-2">
        <div className="relative rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
          <img src={value.previewUrl} alt="" className="w-full max-h-64 object-contain bg-slate-50 dark:bg-slate-800" />
        </div>
        <div className="flex justify-end">
          <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-status-down">
            <Trash2 size={12} /> {t('admin:wizard.image_change')}
          </button>
        </div>
      </div>
    )
  }

  if (src) {
    return (
      <div className="space-y-2">
        <div className="relative h-64 rounded-lg overflow-hidden bg-slate-900">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
          />
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range" min={1} max={4} step={0.05} value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            className="flex-1 accent-[#017f96]"
            aria-label={t('admin:wizard.zoom')}
          />
          <button type="button" onClick={() => setRotation(r => (r + 90) % 360)} title={t('admin:wizard.rotate')}
            className="text-slate-500 dark:text-slate-400 hover:text-brand-600"><RotateCw size={16} /></button>
          <button type="button" onClick={clear} className="text-xs text-slate-500 dark:text-slate-400 hover:text-status-down">{t('common:actions.cancel')}</button>
          <button type="button" onClick={confirm} disabled={busy}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
            {busy ? t('admin:wizard.cropping') : t('admin:wizard.crop')}
          </button>
        </div>
        {error && <p className="text-[11px] text-status-down">{error}</p>}
      </div>
    )
  }

  return (
    <div>
      <label onDrop={pick} onDragOver={e => e.preventDefault()}
        className="flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg px-3 py-8 text-center cursor-pointer hover:border-brand/50">
        <Upload size={20} className="text-slate-400 dark:text-slate-500" />
        <span className="text-xs text-slate-400 dark:text-slate-500">{t('admin:wizard.image_drop')}</span>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} />
      </label>
      {error && <p className="mt-1 text-[11px] text-status-down">{error}</p>}
      {hint && <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{hint}</p>}
    </div>
  )
}

// Desenha a área recortada (com rotação) num canvas e exporta JPEG (qualidade 0.9).
// Todos os uploads viram JPEG: nome determinístico no asset pack (model/<slot>.jpg).
async function cropToJpeg(src, area, rotation) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image()
    i.onload = () => resolve(i)
    i.onerror = reject
    i.src = src
  })

  const rad = (rotation * Math.PI) / 180
  // canvas intermediário com a imagem inteira rotacionada (bounding box)
  const bw = Math.abs(Math.cos(rad) * img.width) + Math.abs(Math.sin(rad) * img.height)
  const bh = Math.abs(Math.sin(rad) * img.width) + Math.abs(Math.cos(rad) * img.height)
  const full = document.createElement('canvas')
  full.width = bw; full.height = bh
  const fctx = full.getContext('2d')
  fctx.translate(bw / 2, bh / 2)
  fctx.rotate(rad)
  fctx.drawImage(img, -img.width / 2, -img.height / 2)

  const out = document.createElement('canvas')
  const MAX = 1600 // limita a resolução final (pack leve pro VMC)
  const scale = Math.min(1, MAX / Math.max(area.width, area.height))
  out.width = Math.round(area.width * scale)
  out.height = Math.round(area.height * scale)
  const ctx = out.getContext('2d')
  ctx.drawImage(full, area.x, area.y, area.width, area.height, 0, 0, out.width, out.height)

  return new Promise((resolve, reject) => {
    out.toBlob(b => (b ? resolve(b) : reject(new Error('falha ao gerar JPEG'))), 'image/jpeg', 0.9)
  })
}

// Sobe o blob recortado para /api/images e devolve {id, url, ...}.
export async function uploadImage(value, kind, slot) {
  const fd = new FormData()
  fd.append('file', value.blob, 'image.jpg')
  fd.append('kind', kind)
  if (slot) fd.append('slot', slot)
  const res = await fetch('/api/images', { method: 'POST', body: fd })
  if (!res.ok) throw await apiError(res)
  return res.json()
}
